'use client';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useParams } from 'next/navigation';
import { FormMode, Messages, OrganizationIdName } from '@efacity/common';
import { initialUserFormValues, UserFormValues, getMergedFormValues, WithLoading } from '@efacity/frontend-shared';
import { addServerErrors } from '@efacity/react-hook-form-mui';
import { UserSettingsForm } from './UserSettingsForm';
import { userSettingsFields } from './UserSettingsFormValues';
import { showNotification } from '@efacity/frontend-next-shared/notifications';
import { apiService } from '@efacity/react-next-sc';
import { PATHS, toPath } from '@efacity/routing';
import { useRouter } from 'next/navigation';
import { CenteredFormContainer } from '@efacity/frontend-next-shared/forms/server';
import useSWR from 'swr';
import { AxiosResponse } from 'axios';

export interface UserFormProps {
  mode: FormMode;
}

type ParamTypes = Partial<{
  id: string;
}>;

export const SiteAdminUserForm: React.FC<UserFormProps> = ({ mode }) => {
  const { id: userId } = useParams<ParamTypes>();

  const router = useRouter();

  const userFetchURL = userId ? `/users/${userId}` : null;

  //https://github.com/vercel/swr/blob/main/examples/axios-typescript/libs/useRequest.ts
  const {
    data: { data: userFormValues },
    isLoading: isUserLoading
  } = useSWR<AxiosResponse<UserFormValues>>(userFetchURL, (url: string) => apiService().get<UserFormValues>(url), {
    fallbackData: {
      data: initialUserFormValues,
      status: 200,
      statusText: 'InitialData',
      config: {},
      headers: {}
    } as AxiosResponse<UserFormValues>
  });

  const { data: organizationOptions, isLoading: isOrganizationLoading } = useSWR('/org/organizations', (url) =>
    apiService().get<OrganizationIdName[]>(url)
  );

  const handleSubmit = async (
    formValues: UserFormValues,
    setError: (fieldName: keyof UserFormValues, error: { type: string; message: string }) => void
  ) => {
    const userActionSuccessful = (message: string) => {
      showNotification(true, message);
      router.push(toPath(PATHS.siteAdminUsers));
    };

    const userActionFailure = (error: { response: { data: { message: string; validationErrors: any[] } } }) => {
      const { data: errorData } = error.response;
      if (errorData.validationErrors) {
        addServerErrors<UserFormValues>(errorData.validationErrors, setError);
      } else {
        showNotification(false, error.response?.data?.message || Messages.FailedUpdateInstructor, true);
      }
    };

    if (mode === FormMode.Edit) {
      return apiService()
        .patch<{ message: string }, UserFormValues>(`/users/${userId}`, formValues)
        .then(({ data }) => {
          userActionSuccessful(data.message);
        })
        .catch((error) => {
          userActionFailure(error);
        });
    } else {
      return apiService()
        .post<{ message: string }, UserFormValues>('/users', formValues)
        .then(({ data }) => {
          userActionSuccessful(data.message as string);
        })
        .catch((error) => {
          userActionFailure(error);
        });
    }
  };

  const handleCancel = () => {
    router.push(toPath(PATHS.siteAdminUsers));
  };

  return (
    <CenteredFormContainer>
      <Box marginBottom={4}>
        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
          {mode === FormMode.Edit ? 'Edit User' : 'Add User'}
          <hr style={{ height: '2', width: 'auto', margin: 0 }} />
        </Typography>
      </Box>
      <WithLoading
        isLoading={isUserLoading || isOrganizationLoading}
        message={'Loading user settings...'}
        style={{ width: '50%' }}
      >
        <UserSettingsForm
          initialFormValues={getMergedFormValues(initialUserFormValues, userFormValues)}
          organizationOptions={organizationOptions?.data || []}
          allowHandleAccess={true}
          allowChangeEmail={true}
          allowChangePassword={true}
          handleSubmit={handleSubmit}
          isSubmitDisabled={isUserLoading}
          handleCancel={handleCancel}
          userModelFields={userSettingsFields}
          formMode={mode}
        />
      </WithLoading>
    </CenteredFormContainer>
  );
};
